// Pure, IO-free mirror of the backend RegistrationServiceImpl.scanQR business rules.
// Keeping this pure makes the offline behavior testable without IndexedDB/network
// and keeps the decision logic in one place so it can be diffed against the backend.
//
// Backend reference: event-registration-be RegistrationServiceImpl.scanQR (blacklist,
// alreadyUsed + allowMultipleScanning, checkin recording).

/**
 * Build a lookup of blacklisted purposes from an event form schema.
 * Mirrors the backend blackListMap: field label (lowercased) + option label (lowercased)
 * -> list of blacklisted purposes.
 */
function buildBlacklist(formSchema = []) {
  const map = {};
  for (const field of formSchema || []) {
    const label = field?.label;
    const options = field?.optionWithBlackLists;
    if (!label || !Array.isArray(options) || options.length === 0) continue;
    for (const opt of options) {
      const purposes = opt?.blackListedQrPurposes;
      if (!Array.isArray(purposes) || purposes.length === 0) continue;
      const optLabel = (opt?.label ?? '').toString().toLowerCase();
      map[label] = map[label] || {};
      map[label][optLabel] = purposes;
    }
  }
  return map;
}

/**
 * Decide the outcome of an offline scan without performing any IO.
 *
 * @param {object} args
 * @param {object|null} args.registration cached registration ({ userId, rfId, formData, checkins, multipleCheckins })
 * @param {object|undefined} args.purposeConfig cached QR purpose config for this purpose
 * @param {Array} args.formSchema cached event form schema (for blacklist rules)
 * @param {string} args.purpose scan purpose key
 * @param {string} args.userId resolved user id (already decoded)
 * @param {string} [args.actorId] operator id recording the scan
 * @param {Date}   [args.now] injectable clock for tests
 * @returns {{ recorded: boolean, mutation: object|null, response: object }}
 */
export const SCAN_BLOCKED_STATUSES = {
  CANCELLED: 'Registration cancelled',
  REJECTED: 'Registration rejected',
  PENDING_PAYMENT: 'Payment not completed',
  PAYMENT_FAILED: 'Payment not completed',
};

export function evaluateScan({
  registration,
  purposeConfig,
  formSchema = [],
  purpose,
  userId,
  actorId = null,
  now = new Date(),
}) {
  const usedAt = now.toISOString();

  if (!userId) {
    return {
      recorded: false,
      mutation: null,
      response: { error: 'User not found', message: 'User not found', purpose },
    };
  }

  if (!registration) {
    return {
      recorded: false,
      mutation: null,
      response: {
        error: 'User not registerd',
        message: 'User not registered',
        userId,
        purpose,
      },
    };
  }

  // Mirrors the backend: cancelled (incl. refunded), rejected or unpaid registrations can't check in.
  const blocked = SCAN_BLOCKED_STATUSES[String(registration.status || '').toUpperCase()];
  if (blocked) {
    return {
      recorded: false,
      mutation: null,
      response: { error: blocked, message: blocked, userId: registration.userId || userId, formData: registration.formData || {}, purpose },
    };
  }

  // Blacklist check (mirrors backend: value of a form field blacklisted for this purpose).
  const blacklist = buildBlacklist(formSchema);
  const formData = registration.formData || {};
  for (const fieldLabel of Object.keys(formData)) {
    const optionMap = blacklist[fieldLabel];
    if (!optionMap) continue;
    const value = (formData[fieldLabel] ?? '').toString().toLowerCase();
    const blacklistedPurposes = optionMap[value];
    if (Array.isArray(blacklistedPurposes) && blacklistedPurposes.includes(purpose)) {
      return {
        recorded: false,
        mutation: null,
        response: { error: 'Not allowed', message: 'Not allowed', userId, purpose },
      };
    }
  }

  const checkins = registration.checkins || {};
  const existing = checkins[purpose];
  const alreadyUsed = !!(existing && existing.used === true);
  const allowMultipleScans = !!(purposeConfig && purposeConfig.allowMultipleScanning);

  if (alreadyUsed && !allowMultipleScans) {
    // Backend records an extra entry in multipleCheckins and returns "Already redeemed".
    const priorList = (registration.multipleCheckins && registration.multipleCheckins[purpose]) || [];
    const newCheckin = {
      used: true,
      usedAt,
      purpose,
      actorId,
      scanCount: priorList.length + 1,
    };
    return {
      recorded: true,
      mutation: { type: 'multipleCheckin', purpose, checkin: newCheckin },
      response: {
        error: 'Already redeemed',
        message: `Already redeemed for ${purpose}`,
        userId,
        formData,
        alreadyUsed: true,
        purpose,
        redeemedAt: existing.usedAt || null,
        redeemedBy: existing.actorId || null,
        previousScanCount: Math.max(1, Number(existing.scanCount) || 1),
        lastScannedAt: existing.usedAt || null,
      },
    };
  }

  // Fresh checkin (or allowed multiple scan) -> success; repeat scans keep a running count.
  const previousScanCount = alreadyUsed ? Math.max(1, Number(existing.scanCount) || 1) : 0;
  const newCheckin = { used: true, usedAt, purpose, actorId, scanCount: previousScanCount + 1 };
  return {
    recorded: true,
    mutation: { type: 'checkin', purpose, checkin: newCheckin },
    response: {
      userId: registration.userId,
      formData,
      alreadyUsed: false,
      purpose,
      // Offline cannot regenerate the server QR PNG; reuse cached one if available.
      // ponytail: badge QR image may be missing offline (ceiling); upgrade path is a
      // client-side QR generator lib if offline badge printing needs the QR rendered.
      qrCodeBase64: registration.qrCodeBase64 || null,
      previousScanCount,
      lastScannedAt: alreadyUsed ? existing.usedAt || null : null,
    },
  };
}

/**
 * Apply a mutation returned by evaluateScan to a (cloned) registration object.
 * Returns the updated registration; does not persist.
 */
export function applyMutation(registration, mutation) {
  if (!mutation) return registration;
  const next = { ...registration };
  if (mutation.type === 'checkin') {
    next.checkins = { ...(next.checkins || {}), [mutation.purpose]: mutation.checkin };
  } else if (mutation.type === 'multipleCheckin') {
    const map = { ...(next.multipleCheckins || {}) };
    const list = Array.isArray(map[mutation.purpose]) ? map[mutation.purpose].slice() : [];
    list.push(mutation.checkin);
    map[mutation.purpose] = list;
    next.multipleCheckins = map;
  }
  return next;
}

export const __testables = { buildBlacklist };
